import { Account, Pair } from '@api/core';
import { isDefined, Maybe } from '@stillmandigital/common';
import BigNumber from 'bignumber.js';
import { isNil, isString } from 'lodash';

const MULTIPLIER = 1000;

export function toViewRateValue<T extends Maybe<number>>(rate: T, multiplier = MULTIPLIER): typeof rate {
  if (isDefined(rate)) {
    return new BigNumber(rate).dividedBy(multiplier).toNumber() as NonNullable<T>;
  }
  return rate;
}

export function toApiRateValue<T extends Maybe<number>>(rate: T, multiplier = MULTIPLIER): typeof rate {
  if (isDefined(rate)) {
    return new BigNumber(rate).times(multiplier).toNumber() as NonNullable<T>;
  }
  return rate;
}

export function toViewSpreadBpsValue(askPrice: number, bidPrice: number): string {
  if (!isDefined(askPrice) || !isDefined(bidPrice)) {
    return '';
  }

  const baseSpread = BigNumber(askPrice).minus(bidPrice);
  const middlePrice = BigNumber(askPrice).plus(bidPrice).div(2);

  return baseSpread.div(middlePrice).multipliedBy(10000).toFixed(1);
}

function parseBpsValue(bps: Maybe<number | string>): Maybe<BigNumber> {
  const value = isString(bps) ? bps.trim() : bps;

  if (isNil(value) || value === '' || !Number.isFinite(Number(value))) {
    return undefined;
  }

  return new BigNumber(value);
}

export function toViewBpsValue(bps: Maybe<number | string>): number {
  return parseBpsValue(bps)?.div(10).toNumber() ?? 0;
}

export function toApiBpsValue(bps: Maybe<number | string>): number {
  return parseBpsValue(bps)?.times(10).toNumber() ?? 0;
}

export function toFeeBpsValue(
  fee: Maybe<number>,
  feeAssetRefPriceUsd: Maybe<number>,
  volume: Maybe<number>,
  quoteAssetRefPriceUsd: Maybe<number>,
): Maybe<number> {
  if (!isDefined(fee) || !isDefined(volume) || volume === 0) {
    return undefined;
  }

  const feeUsd = BigNumber(fee).times(feeAssetRefPriceUsd ?? 1);
  const volumeUsd = BigNumber(volume).times(quoteAssetRefPriceUsd ?? 1);

  return volumeUsd.isZero() ? undefined : feeUsd.div(volumeUsd).times(10000).toNumber();
}

export function getCommissionRate(account?: Account, pair?: Pair, customCommissionRate?: number): number {
  return isDefined(customCommissionRate)
    ? customCommissionRate
    : pair?.stableCoin
      ? (account?.stableCoinCommissionRate ?? 0)
      : (account?.commissionRate ?? 0);
}

export interface BuySellCommissionRates {
  buyCommissionRate: number;
  sellCommissionRate: number;
}

export function getBuySellCommissionRates(
  account?: Account,
  pair?: Pair,
  customOverride?: { askCommissionRate?: number; bidCommissionRate?: number },
): BuySellCommissionRates {
  const fallback = pair?.stableCoin ? (account?.stableCoinCommissionRate ?? 0) : (account?.commissionRate ?? 0);

  return {
    buyCommissionRate: customOverride?.askCommissionRate ?? fallback,
    sellCommissionRate: customOverride?.bidCommissionRate ?? fallback,
  };
}
